import React, { isValidElement } from "react";
import Box from "@mui/material/Box";
import { IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Delete02Icon } from "@hugeicons-pro/core-stroke-rounded";

export interface RemovableFieldProps {
  /** The field being made removable (a DS TextField, Select, etc). */
  children: React.ReactNode;
  /** Omit to render no button: the handler is what gates the affordance. */
  onRemove?: () => void;
  disabled?: boolean;
  /** Translated by the caller; the DS ships no i18n. */
  removeLabel: string;
}

/** DS field heights, per the TextField `size` docs. */
const FIELD_HEIGHT = { md: 36, lg: 44 } as const;

/**
 * Pairs a field with a trash button that removes the row it represents. Fell
 * out of the DS TextField retarget (OUT-1103), which left the DS field with no
 * delete affordance.
 *
 * Not expressible as the field's `onClear`: the DS hides the clear x until the
 * field has a value, and both callers create empty rows that must stay
 * removable.
 *
 * The button belongs beside the INPUT, not beside label + input + helper, so it
 * reads the child's `label` and `size` to skip the label row (the same
 * child-introspection the DS FormField does for `htmlFor`). That reach into DS
 * field metrics is the argument for a trailing-action slot in the DS itself.
 */
const RemovableField = ({
  children,
  onRemove,
  disabled,
  removeLabel,
}: RemovableFieldProps) => {
  if (!onRemove) return <>{children}</>;

  const field = isValidElement<{ label?: React.ReactNode; size?: "md" | "lg" }>(
    children,
  )
    ? children
    : undefined;
  const labelOffset = field?.props.label
    ? "calc(var(--type-line-height-tight) + var(--space-3))"
    : 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--space-2)",
        width: "100%",
      }}
    >
      <Box
        sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}
      >
        {children}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          marginTop: labelOffset,
          height: FIELD_HEIGHT[field?.props.size ?? "lg"],
        }}
      >
        <IconButton
          size="small"
          color="danger"
          aria-label={removeLabel}
          onClick={onRemove}
          disabled={disabled}
        >
          <HugeiconsIcon icon={Delete02Icon} size={16} color="currentColor" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default RemovableField;
